"use client"

import { useState, Suspense } from "react"
import Link from "next/link"
import Image from "next/image"
import { useRouter, useSearchParams } from "next/navigation"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Eye, EyeOff, Loader2, Building2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { toast } from "sonner"

// Form validation schema
const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
})

type LoginFormData = z.infer<typeof loginSchema>

function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectUrl = searchParams.get("redirect")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true)

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        toast.error(result.message || "Login failed")
        return
      }

      // Store tokens
      if (result.tokens?.accessToken) {
        localStorage.setItem("accessToken", result.tokens.accessToken)
        if (result.tokens.refreshToken) {
          localStorage.setItem("refreshToken", result.tokens.refreshToken)
        }
      }

      toast.success("Welcome back!")

      // Redirect to original URL if provided, otherwise based on role
      if (redirectUrl) {
        router.push(redirectUrl)
      } else if (result.user?.role === "superadmin" || result.user?.role === "super-admin" || result.user?.role === "admin") {
        router.push("/admin/dashboard")
      } else {
        router.push("/dashboard")
      }
    } catch {
      toast.error("An error occurred. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-center gap-8 w-full max-w-md px-4">
      {/* Spire Group Logo */}
      <Link href="/" className="flex items-center mt-8 group">
        <Image
          src="/spire-logo-black.webp"
          alt="Spire Group"
          width={200}
          height={50}
          className="h-12 w-auto"
        />
      </Link>

      <Card className="w-full rounded-xl shadow-lg border-2 border-red-50">
        <CardHeader className="space-y-2 p-6 md:p-8 bg-gradient-to-br from-red-50 to-white rounded-t-xl">
          <CardTitle className="text-2xl md:text-3xl font-bold text-center text-[#d60500] font-heading">
            Welcome back
          </CardTitle>
          <CardDescription className="text-center text-base text-gray-600">
            Sign in to your admin account
          </CardDescription>
        </CardHeader>

      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-5 p-6 md:p-8 pt-0">
          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-base font-semibold">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              disabled={isLoading}
              {...register("email")}
              className="h-12 rounded-xl text-base"
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-base font-semibold">Password</Label>
              <Link
                href="/forgot-password"
                className="text-sm text-[#d60500] hover:underline font-medium"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={isLoading}
                {...register("password")}
                className="h-12 rounded-xl text-base pr-12"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
                <span className="sr-only">
                  {showPassword ? "Hide password" : "Show password"}
                </span>
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">
                {errors.password.message}
              </p>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-6 p-6 md:p-8 bg-gray-50 rounded-b-xl">
          <Button
            type="submit"
            className="w-full h-12 rounded-none text-base font-semibold bg-[#d60500] hover:bg-[#b50400] text-white uppercase tracking-wider"
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
            Sign in
          </Button>

          <p className="text-sm text-center text-gray-600">
            Don&apos;t have an account?{" "}
            <Link href="/signup" className="text-[#d60500] hover:underline font-semibold">
              Sign up
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
    </div>
  )
}

// Wrap with Suspense for useSearchParams
export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    }>
      <LoginForm />
    </Suspense>
  )
}
